/**
 * English catalogue.
 *
 * Intentionally allowed to be incomplete: a missing key falls back to Arabic
 * (see `I18nProvider`), which keeps a partially translated build usable rather
 * than showing raw keys to a clinician mid-shift.
 */
import { authEn } from './auth.en';
import { flowEn } from './flow.en';
import { appointmentsEn } from './appointments.en';
import { platformEn } from './platform.en';
import { diagnosticCasesEn } from './diagnosticCases.en';

export const en: Record<string, string> = {
  // EPIC AUTH owns its strings in auth.en.ts.
  ...authEn,
  // EPIC FLOW owns its strings in flow.en.ts.
  ...flowEn,
  // EPIC APT owns its strings in appointments.en.ts.
  ...appointmentsEn,

  // EPIC DXC owns its strings in diagnosticCases.en.ts.
  ...diagnosticCasesEn,

  // EPIC PLAT owns its strings in platform.en.ts.
  ...platformEn,

  'app.name': 'Clinvisor',
  'app.search.placeholder': 'Search patients, appointments, invoices…',
  'app.search.scopeNote': 'Results are limited to your permissions and active branch',
  'app.allScreens': 'All screens',
  'app.activeBranch': 'Active branch',
  'app.signOut': 'Sign out',
  'app.notifications': 'Notifications',
  'app.notifications.all': 'All',
  'app.notifications.critical': 'Critical',
  'app.notifications.finance': 'Finance',
  'app.notifications.empty': 'No notifications',
  'app.skipToContent': 'Skip to content',
  'app.loading': 'Loading…',

  'nav.dashReception': "Today's board",
  'nav.dashDoctor': 'Doctor board',
  'nav.appointments': 'Appointments',
  'nav.waiting': 'Waiting room',
  'nav.patients': 'Patients',
  'nav.visit': 'Visit centre',
  'nav.lab': 'Laboratory',
  'nav.imaging': 'Imaging',
  'nav.pharmacy': 'Pharmacy',
  'nav.checkout': 'Checkout',
  'nav.invoices': 'Invoices',
  'nav.expenses': 'Expenses',
  'nav.reports': 'Reports',
  'nav.users': 'Users',
  'nav.doctors': 'Doctors & schedules',
  'nav.branches': 'Branches',
  'nav.services': 'Services & prices',
  'nav.reference': 'Medical reference',
  'nav.ai': 'AI assistant',
  'nav.onboarding': 'Setup checklist',
  'nav.settings': 'Settings',
  'nav.subscription': 'Subscription',
  'nav.audit': 'Audit log',
  'nav.platformTenants': 'Tenants',
  'nav.platformHealth': 'Platform health',
  'nav.platformLeads': 'Demo requests',
  'nav.plans': 'Plans & limits',
  'nav.platformAudit': 'Platform audit',

  'role.OWNER': 'Clinic owner',
  'role.ADMIN': 'Clinic administrator',
  'role.DOCTOR': 'Doctor',
  'role.RECEPTION': 'Reception',
  'role.ACCOUNTING': 'Accounting',
  'role.LAB': 'Laboratory',
  'role.RADIOLOGY': 'Radiology',
  'role.PHARMACY': 'Pharmacy',
  'role.MEMBER': 'Member',
  'role.platform': 'Platform support',

  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.confirm': 'Confirm',
  'action.close': 'Close',
  'action.retry': 'Retry',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.export': 'Export',
  'action.print': 'Print',
  'action.search': 'Search',
  'action.filter': 'Filter',
  'action.back': 'Back',
  'action.next': 'Next',
  'action.add': 'Add',

  'state.empty': 'No data',
  'state.error': 'Could not load data',
  'state.errorBody': 'An unexpected error occurred. You can retry.',
  'state.forbidden': 'Not authorised',
  'state.forbiddenBody': 'You do not have permission to view this screen.',
  'state.correlationId': 'Request ID',
  'state.conflict': 'Another user changed this record after you opened it.',
  'state.subscriptionBlocked': 'This feature is not in your plan',
  'state.upgrade': 'Upgrade plan',
  'state.offline': 'Live updates unavailable — refreshing every 30 seconds',

  'appointmentStatus.REQUESTED': 'Requested',
  'appointmentStatus.CONFIRMED': 'Confirmed',
  'appointmentStatus.ARRIVED': 'Arrived',
  'appointmentStatus.IN_PROGRESS': 'In consultation',
  'appointmentStatus.READY_CHECKOUT': 'Ready for checkout',
  'appointmentStatus.COMPLETED': 'Completed',
  'appointmentStatus.CANCELLED': 'Cancelled',
  'appointmentStatus.NO_SHOW': 'No show',

  'visitStatus.DRAFT': 'Draft',
  'visitStatus.FINALISED': 'Finalised',
  'visitStatus.AMENDED': 'Amended',
  'visitStatus.VOIDED': 'Voided',

  'invoiceStatus.DRAFT': 'Draft',
  'invoiceStatus.ISSUED': 'Issued',
  'invoiceStatus.PARTIAL': 'Partially paid',
  'invoiceStatus.PAID': 'Paid',
  'invoiceStatus.VOID': 'Void',
  'invoiceStatus.REFUNDED': 'Refunded',

  'impersonation.title': 'Platform support mode — working inside “{tenant}”',
  'impersonation.purpose': 'Purpose: {purpose}',
  'impersonation.end': 'End session',

  // --- Coded terminology (BUG-05) -------------------------------------------
  'term.label': 'Diagnosis',
  'term.placeholder': 'Search by name or code…',
  'term.placeholderFreeText': 'Type the diagnosis…',
  'term.results': 'Search results',
  'term.searching': 'Searching…',
  'term.searchFailed': 'The catalogue could not be searched. You can still type the diagnosis.',
  'term.useFreeText': 'Use what I typed:',
  'term.useFreeTextHint': 'Saved without a diagnosis code.',
  'term.uncoded': 'Uncoded',
  'term.clear': 'Clear selection',
  'term.clinicAlias': 'synonym',
  'term.catalogueEmpty':
    'No diagnosis catalogue has been imported yet, so diagnoses are saved as text without a code.',
};
